import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import qmsDocumentActions from "../../../_actions/_qmsActions/qmsDocumentActions";
import qmsDocumentTypeActions from "../../../_actions/_qmsActions/qmsDocumentTypeActions";
import _ from "lodash";
import { QMS_DOCTYPE_SYMBOL } from "../_common/qmsConstants";
import { getQmsDocumentLabel } from "../_common/qmsUtilities";

export default function CmtLinkedDocumentEditor(props) {
  const { divisionId, data, onUpdate } = props;

  const dispatch = useDispatch();
  const qmsDocuments = useSelector((state) => state.qms.qmsDocuments[divisionId] || null);
  const qmsDocumentTypes = useSelector(
    (state) => state.qms.qmsDocumentTypes[divisionId] || null
  );

  useEffect(() => {
    dispatch(qmsDocumentActions.getAll(divisionId));
    dispatch(qmsDocumentTypeActions.getAll(divisionId));
  }, []);

  const [linkedDocClient, setLinkedDocClient] = useState({});
  const [hasOtherLinkedDocs, setHasOtherLinkedDocs] = useState(false);
  const [otherLinkedDocuments, setOtherLinkedDocuments] = useState("");

  const qmsDocumentOptions = useMemo(() => {
    return qmsDocumentTypes
      ? (qmsDocuments || []).map((qd) => {
          return {
            label: getQmsDocumentLabel(qd, qmsDocumentTypes),
            value: qd.qmsDocumentId,
            qmsDocumentTypeId: qd.qmsDocumentTypeId,
          };
        })
      : [];
  }, [qmsDocuments, qmsDocumentTypes]);

  // When qmsDocumentTypes becomes available, prepare the list of standard document types
  // (all with non-null symbol).
  useEffect(() => {
    const _linkedDocClient = {};
    (qmsDocumentTypes || []).forEach((qdt) => {
      if (qdt.symbol !== null) {
        // Filter on:
        // 1) qmsDoc exists,
        // 2) qmsDoc's type matches currently iterating qdt, and
        // 3) qmsDocOption exists
        const docTypeFilter = (ld) => {
          const qd = (qmsDocuments || []).find(
            (qd) => qd.qmsDocumentId === ld.qmsDocumentId
          );
          const qdoExists = (qmsDocumentOptions || []).some(
            (qdo) => qdo.value === ld.qmsDocumentId
          );
          return qd && qd.qmsDocumentTypeId === qdt.qmsDocumentTypeId && qdoExists;
        };

        const getSections = () => {
          if (!data) return "";
          // Just get any with sections populated
          const ldsWithSections = (data.linkedDocuments || []).filter(
            (ld) => docTypeFilter(ld) && ld.sections
          );
          return ldsWithSections.length > 0 ? ldsWithSections[0].sections : "";
        };

        _linkedDocClient[QMS_DOCTYPE_SYMBOL[qdt.symbol]] = {
          qmsDocumentTypeId: qdt.qmsDocumentTypeId,
          isSelected:
            data && "linkedDocuments" in data
              ? (data.linkedDocuments || []).some((ld) => docTypeFilter(ld))
              : false, // State of checkboxes
          label: qdt.label, // Checkbox labels
          // "Sections and Notes" input value
          sections: getSections(),
          // Document selector values (multiselect)
          selectedDocuments:
            // If data is provided, prepopulate
            data && "linkedDocuments" in data
              ? (data.linkedDocuments || [])
                  .filter((ld) => docTypeFilter(ld))
                  .map((ld) => {
                    return (qmsDocumentOptions || []).find(
                      (qdo) => qdo.value === ld.qmsDocumentId
                    );
                  })
              : [],
        };
      }
    });
    setLinkedDocClient(_linkedDocClient);
  }, [qmsDocumentTypes, qmsDocuments, qmsDocumentOptions]);

  const handleChange_check = (docSymbol) => (isSelected) =>
    setLinkedDocClient({
      ...linkedDocClient,
      [docSymbol]: {
        ...linkedDocClient[docSymbol],
        isSelected,
        sections: !isSelected ? "" : linkedDocClient[docSymbol].sections,
        selectedDocuments: !isSelected
          ? []
          : linkedDocClient[docSymbol].selectedDocuments,
      },
    });

  const handleChange_docs = (docSymbol) => (selectedDocuments) => {
    setLinkedDocClient({
      ...linkedDocClient,
      [docSymbol]: { ...linkedDocClient[docSymbol], selectedDocuments },
    });
  };

  const handleChange_sections = (docSymbol) => (e) =>
    setLinkedDocClient({
      ...linkedDocClient,
      [docSymbol]: { ...linkedDocClient[docSymbol], sections: e.target.value },
    });

  const handleUpdate = () => {
    let _linkedDocs = [];
    for (const qdtSymbol in linkedDocClient) {
      const sections = linkedDocClient[qdtSymbol].sections || "";
      const selectedDocsForThisType = (
        linkedDocClient[qdtSymbol].selectedDocuments || []
      ).map((d) => ({
        issueLinkedDocumentId: null,
        qmsDocumentId: d.value,
        sections,
      }));
      if (data) {
        // Compare with existing to get the proper linkedDocumentId
        selectedDocsForThisType.forEach((d) => {
          const matchingExistLd = (data.linkedDocuments || []).find(
            (ld) => ld.qmsDocumentId === d.qmsDocumentId
          );
          if (matchingExistLd)
            d.issueLinkedDocumentId = matchingExistLd.issueLinkedDocumentId;
        });
      }
      _linkedDocs = [..._linkedDocs, ...selectedDocsForThisType];
    }

    onUpdate({ linkedDocuments: _linkedDocs, otherLinkedDocuments });
  };

  return (
    <>
      {Object.entries(linkedDocClient).map(([docSymbol, linkedDocClientRow], index) => (
        <div key={`impactedDocInfoKey_${index}`} className="row mb-2">
          <div className="form-group col-md-3 mb-0 pr-2 my-auto">
            <ImpactedDocInfoCheck
              docSymbol={docSymbol}
              checked={linkedDocClientRow.isSelected}
              handleChange={handleChange_check(docSymbol)}
            >
              {linkedDocClientRow.label}
            </ImpactedDocInfoCheck>
          </div>
          <div className="form-group col-md-5 mb-0 pr-2 my-auto">
            <Select
              name={`${docSymbol}_selectedDocuments`}
              id={`${docSymbol}_selectedDocuments`}
              value={linkedDocClientRow.selectedDocuments}
              options={(qmsDocumentOptions || []).filter(
                (qdo) => qdo.qmsDocumentTypeId === linkedDocClientRow.qmsDocumentTypeId
              )}
              onChange={handleChange_docs(docSymbol)}
              isClearable
              isMulti
              onBlur={handleUpdate}
              isDisabled={!linkedDocClientRow.isSelected}
              styles={{
                // Fixes the overlapping problem of the component
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
            />
          </div>
          <div className="form-group col-md-4 mb-0 pl-0 my-auto">
            <input
              name={`${docSymbol}_sections`}
              id={`${docSymbol}_sections`}
              className="form-control"
              value={linkedDocClientRow.sections || ""}
              onChange={handleChange_sections(docSymbol)}
              placeholder="Notes, subsections, etc."
              onBlur={handleUpdate}
              disabled={!linkedDocClientRow.isSelected}
            />
          </div>
        </div>
      ))}
      <div className="row mb-0">
        <div className="form-group col-md-3 mb-0 pr-2 my-auto">
          <div className="custom-control-inline custom-control custom-checkbox">
            <input
              type="checkbox"
              name="hasOtherLinkedDocs"
              id="hasOtherLinkedDocs"
              className="custom-control-input my-auto"
              value={hasOtherLinkedDocs}
              checked={hasOtherLinkedDocs}
              onChange={(e) => setHasOtherLinkedDocs(e.target.checked)}
            />
            <label htmlFor="hasOtherLinkedDocs" className="custom-control-label my-auto">
              Other
            </label>
          </div>
        </div>
        <div className="form-group col-md-9 mb-0">
          <input
            name="otherLinkedDocuments"
            id="otherLinkedDocuments"
            className="form-control"
            value={otherLinkedDocuments || ""}
            onChange={(e) => setOtherLinkedDocuments(e.target.value)}
            placeholder="Document title/description, sections, etc."
            onBlur={handleUpdate}
            disabled={!hasOtherLinkedDocs}
          />
        </div>
      </div>
    </>
  );
}

function ImpactedDocInfoCheck(props) {
  const { docSymbol, checked, handleChange } = props;

  return (
    <div className="custom-control-inline custom-control custom-checkbox">
      <input
        type="checkbox"
        name={`${docSymbol}_check`}
        id={`${docSymbol}_check`}
        className="custom-control-input my-auto"
        value={checked}
        checked={checked}
        onChange={(e) => handleChange(e.target.checked)}
        // onBlur={}
      />
      <label htmlFor={`${docSymbol}_check`} className="custom-control-label my-auto">
        {props.children}
      </label>
    </div>
  );
}
